import {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';

import { FormApproveRejectRequest } from '@Types/api/form';
import { ApprovalsListResponse } from '@Types/api/approvalsList';
import {
  useFormApproveMutation,
  useGetTaskListDashboardQuery,
  useItRequisitionAprroveMutation,
  useRequisitionAprroveMutation,
} from '@Store/api/approvalsListApiSlice';

import ESSFeatureRoutePath from '@Features/router/ess/ESSFeatureRoutePath.enum';

import { NotificationContext } from '@Context/NotificationContext';
import { useAppDispatch, useAppSelector, useUserCollector } from '@Hooks/index';
import { currentUserCollectionSelector } from '@Store/selectors/userInfoSelector';
import { ViewResolver } from '@Components/ViewResolver';
import {
  BSANotification,
  EmailCreationNotification,
  ITAdminNotification,
  PlantEngineerNotification,
  PlantWorkshopManagerNotification,
  PurchaseOfficerNotification,
  RequiredNotification,
  SimAddonNotification,
  SimCardNotification,
} from '@Components/index';
import { stateOpenApproveModal } from '@Store/selectors/approvalsListSelector';
import { approvalsListActions } from '@Store/slices/approvalsListSlice';
import { useFormik } from 'formik';
import {
  useGetExistingPackageMutation,
  useGetItApprovalHistoryMutation,
  useGetITRequestFormQuery,
} from '@Features/EmployeeSelfService/requests/ITRequisition/store/api/itRequisitionApiSlice';
import { RequisitionApproveRejectRequest } from '@Types/api/form/materialRequisition/Approve/RequisitionApproveRequest';

import { ApprovalActions } from '@Enum/ApprovalActions';
import { deciderOfApprovePathName } from '@Utils/deciderOfServicePathName';
import { ItRequisitionTypeCodes } from '@Enum/api/ItRequisitionTypeCodes.enum';
import { ItRequisitionStatusCodes } from '@Enum/api/ItRequisitionStatusCodes';
import { RequisitionStatuses } from '@Features/MaterialRequest/enum/RequisitionStatuses.enum';
import {
  useGetRequisitionQuery,
  useGetApprovalHistoryMutation as useGetMRApprovalHistoryMutation,
  useGetRequisitionCommodityClassQuery,
} from '@Features/MaterialRequest/requests/MaterialRequisition/store/api/MaterialRequestApiSlice';
import { useGetUsersListByNameQuery } from '@Store/api/userApiSlice';
import { useGetApprovalHistoryMutation as useGetESSApprovalHistoryMutation } from '@Store/api/basicHrmApiSlice';
import { useGetResignationApprovalHistoryMutation } from '@Features/EmployeeSelfService/requests/ResignationRequests/store/api/resignationRequestApiSlice';

import { ApprovalHistory } from '@Types/api/requestList';
import { ApprovalHistory as ApprovalHistoryModal } from '@Components/ApprovalHistory';
import { SimCardForm } from '@Components/RequestList/tableActions/TaskList/components/initFormikValues';
import ApprovalsDesktop from './desktop';
import ApprovalsMobile from './mobile';
import useSepareteRequest from './hooks/useSepareteRequest';

import {
  initBSAFormikValues,
  initITAdmin,
  initNewEmailValues,
  initPlantEngineer,
  initPlantWorkshopManager,
  initPurchaseOfficer,
  initSimAddonFormikValues,
  initSimCardValues,
} from './initFormikValues';

interface ActionProps {
  taskId: string | number;
  servicePathName: string;
  action: 'Approve' | 'Reject';
  taskNotification: () => void;
}

interface Params {
  taskId: string;
  requestId: number;
  requestNo?: string | undefined;
  requestTypeId?: string;
}

interface RequestProcessProps {
  body: FormApproveRejectRequest;
  taskNotification: () => void;
}

interface RequisitionProcessProps {
  body: RequisitionApproveRejectRequest;
  taskNotification: () => void;
}

interface HRMProcessProps extends RequestProcessProps {
  servicePathName: string;
}

export interface ApprovalHistoryParams {
  requestId: number;
  requestService?: string;
  requestTypeId?: string;
}

const Approvals: React.FC = () => {
  const {
    taskApprovedNotification,
    taskErrorNotification,
    taskRejectedNotification,
  } = useContext(NotificationContext);
  const [rejectCommentValue, setRejectCommentValue] = useState('');

  const { data, refetch } = useGetTaskListDashboardQuery({});

  const { collectUserInformation } = useUserCollector();
  const [existingPackageValue, setExistingPackageValue] = useState('');
  const { essList, mrList } = useSepareteRequest(data);
  const [routePrefix, setRoutePrefix] = useState('ess');
  const [activeList, setActiveList] = useState<ApprovalsListResponse[]>([]);
  const navigate = useNavigate();

  const [postApproveRejectForm] = useFormApproveMutation();
  const stateOpenModal = useAppSelector(stateOpenApproveModal);
  const dispatch = useAppDispatch();
  const [message, setMessage] = useState('');
  const [title, setTitle] = useState('');
  const [buttonText, setButtonText] = useState('');
  const [ApprovalsParams, setApprovalsParams] = useState<
    Params & ActionProps
  >();
  const [getExistingPackage] = useGetExistingPackageMutation();
  const [requisitionApproveRejectForm] = useRequisitionAprroveMutation();
  const [itReqApproveRejectForm] = useItRequisitionAprroveMutation();
  const { data: itRequestData, isLoading: isItRequestLoading } =
    useGetITRequestFormQuery(String(ApprovalsParams?.requestId), {
      skip: ApprovalsParams?.requestTypeId !== 'ITRequisition',
    });
  const { data: mrRequestData, isLoading: isMRequestLoading } =
    useGetRequisitionQuery(String(ApprovalsParams?.requestId), {
      skip: ApprovalsParams?.requestTypeId !== 'MRRequest',
    });
  const { errorNotification } = useContext(NotificationContext);
  const [getESSApprovalHistory] = useGetESSApprovalHistoryMutation();
  const [getITRequisitionApprovalHistory] = useGetItApprovalHistoryMutation();
  const [getMRApprovalHistory] = useGetMRApprovalHistoryMutation();
  const [getResignationApprovalHistory] =
    useGetResignationApprovalHistoryMutation();
  const [isApprovalHistoryOpen, setIsApprovalHistoryOpen] = useState(false);
  const [approvalHistoryData, setApprovalHistoryData] = useState<
    ApprovalHistory[] | null
  >(null);
  const { data: users = [] } = useGetUsersListByNameQuery(null);
  const userCollection = useAppSelector(currentUserCollectionSelector);

  const { data: isShowITAdmin } = useGetRequisitionCommodityClassQuery(
    mrRequestData?.requisitionId || 0,
    {
      skip:
        mrRequestData?.statusId !==
        RequisitionStatuses.AWAITING_APPROVAL_OF_WAREHOUSE_MANAGER,
    }
  );

  useLayoutEffect(() => {
    if (data) {
      collectUserInformation(data);
    }
  }, [data]);

  const formikBSA = useFormik({
    initialValues: initBSAFormikValues,
    enableReinitialize: true,
    onSubmit: () => undefined,
  });

  const formikSimAddon = useFormik({
    initialValues: initSimAddonFormikValues,
    enableReinitialize: true,
    onSubmit: () => undefined,
  });

  const formikPurchaseOfficer = useFormik({
    initialValues: initPurchaseOfficer,
    enableReinitialize: true,
    onSubmit: () => undefined,
  });

  const formikPlantEngineer = useFormik({
    initialValues: initPlantEngineer,
    enableReinitialize: true,
    onSubmit: () => undefined,
  });

  const formikPlantWorkshopManager = useFormik({
    initialValues: initPlantWorkshopManager,
    enableReinitialize: true,
    onSubmit: () => undefined,
  });

  const formikITAdmin = useFormik({
    initialValues: initITAdmin,
    enableReinitialize: true,
    onSubmit: () => undefined,
  });

  const initialSimCardFormikValue: SimCardForm = useMemo(
    () => ({
      reqCapValue: itRequestData?.reqCapValue || initSimCardValues.reqCapValue,
      mobileNumber:
        itRequestData?.personalMobile || initSimCardValues.mobileNumber,
    }),
    [itRequestData?.personalMobile, itRequestData?.reqCapValue]
  );

  const formikSimCard = useFormik({
    initialValues: initialSimCardFormikValue,
    enableReinitialize: true,
    onSubmit: () => undefined,
  });

  const formikNewEmail = useFormik({
    initialValues: {
      ...initNewEmailValues,
      username: itRequestData?.username ?? '',
    },
    enableReinitialize: true,
    onSubmit: () => undefined,
  });

  useEffect(() => {
    if (itRequestData?.requestId) {
      getExistingPackage(itRequestData.requestId)
        .unwrap()
        .then((response) => setExistingPackageValue(response?.packages || ''));
    }
  }, [itRequestData?.requestId]);

  const divideToFirstThreeItems = useCallback((): void => {
    const firstThreeTasks = essList.concat(mrList).slice(0, 3);
    setActiveList(firstThreeTasks);
  }, [essList, mrList]);

  useEffect(() => {
    divideToFirstThreeItems();
  }, [data]);

  const showModal = useCallback((): void => {
    dispatch(approvalsListActions.setOpenModalApprove(!stateOpenModal));
  }, [dispatch, stateOpenModal]);

  const handleGoToApprovals = useCallback((): void => {
    navigate(`/${routePrefix}/${ESSFeatureRoutePath.APPROVALS}`);
  }, [navigate, routePrefix]);

  const handleESSApprovals = useCallback((): void => {
    setRoutePrefix('ess');
    setActiveList(essList);
  }, [essList]);

  const handleMRApprovals = useCallback((): void => {
    setRoutePrefix('mr');
    setActiveList(mrList);
  }, [mrList]);

  const handleApprovalHistory = useCallback(
    ({ requestId, requestService, requestTypeId }: ApprovalHistoryParams) => {
      if (requestService === 'ESS') {
        getESSApprovalHistory(String(requestId))
          .unwrap()
          .then((approvalHistoryResp) => {
            if (
              Array.isArray(approvalHistoryResp) &&
              approvalHistoryResp.length
            ) {
              setIsApprovalHistoryOpen(true);
              setApprovalHistoryData(approvalHistoryResp);
            }
          })
          .catch(({ data: error }) => {
            errorNotification(error);
          });
      }
      if (requestService === 'ITRequisition') {
        getITRequisitionApprovalHistory(String(requestId))
          .unwrap()
          .then((approvalHistoryResp) => {
            if (
              Array.isArray(approvalHistoryResp) &&
              approvalHistoryResp.length
            ) {
              setIsApprovalHistoryOpen(true);
              setApprovalHistoryData(approvalHistoryResp);
            }
          })
          .catch(({ data: error }) => {
            errorNotification(error);
          });
      }

      if (requestService === 'ess' && requestTypeId === 'Resignation Request') {
        getResignationApprovalHistory(String(requestId))
          .unwrap()
          .then((approvalHistoryResp) => {
            if (
              Array.isArray(approvalHistoryResp) &&
              approvalHistoryResp.length
            ) {
              setIsApprovalHistoryOpen(true);
              setApprovalHistoryData(approvalHistoryResp);
            }
          })
          .catch(({ data: error }) => {
            errorNotification(error);
          });
      }

      if (requestService !== 'ESS' && requestTypeId === 'MRRequest') {
        getMRApprovalHistory(String(requestId))
          .unwrap()
          .then((approvalHistoryResp) => {
            if (
              Array.isArray(approvalHistoryResp) &&
              approvalHistoryResp.length
            ) {
              setIsApprovalHistoryOpen(true);
              setApprovalHistoryData(approvalHistoryResp);
            }
          })
          .catch(({ data: error }) => {
            errorNotification(error);
          });
      }
    },
    [
      errorNotification,
      getESSApprovalHistory,
      getITRequisitionApprovalHistory,
      getMRApprovalHistory,
    ]
  );

  const isNewEmailRequest = useMemo(() => {
    if (
      itRequestData?.reqItemId === ItRequisitionTypeCodes.EMAIL &&
      itRequestData?.statusId ===
        ItRequisitionStatusCodes.AWAITING_FOR_EMAIL_CREATION
    ) {
      return true;
    }

    return false;
  }, [itRequestData?.reqItemId, itRequestData?.statusId]);

  const isSimAddonRequest = useMemo(() => {
    if (
      itRequestData?.reqItemId === ItRequisitionTypeCodes.SIM_ADDON_REQUEST &&
      itRequestData?.statusId === ItRequisitionStatusCodes.AWAITING_APPROVAL &&
      !itRequestData?.recommendPackage
    ) {
      return true;
    }

    return false;
  }, [itRequestData?.reqItemId, itRequestData?.statusId]);

  const isSimCardRequest = useMemo(() => {
    if (itRequestData?.reqItemId === ItRequisitionTypeCodes.NEW_SIM_CARD) {
      if (
        itRequestData?.statusId ===
        ItRequisitionStatusCodes.AWAITING_SIM_CARD_FROM_ETISALAT
      ) {
        return 'Input';
      }

      if (
        itRequestData?.statusId ===
          ItRequisitionStatusCodes.AWAITING_APPROVAL_OF_ADMIN_MANAGER ||
        itRequestData?.statusId ===
          ItRequisitionStatusCodes.AWAITING_APPROVAL_OF_PAYROLL_ADMIN
      ) {
        return 'Dropdown';
      }
    }
    return '';
  }, [itRequestData?.reqItemId, itRequestData?.statusId]);

  const isBSARequest = useMemo(() => {
    if (
      itRequestData?.reqItemId === ItRequisitionTypeCodes.BUILD_SMART_ACCESS &&
      itRequestData?.statusId === ItRequisitionStatusCodes.UNDER_PROCESS
    ) {
      return true;
    }

    return false;
  }, [itRequestData?.statusId, itRequestData?.reqItemId]);

  const isPurchaseOfficerRequest = useMemo(() => {
    if (
      mrRequestData?.statusId ===
      RequisitionStatuses.AWAITING_APPROVAL_OF_PROCUREMENT_MANAGER
    ) {
      return true;
    }
    return false;
  }, [mrRequestData]);

  const isPlantEngineerRequest = useMemo(() => {
    if (
      mrRequestData?.statusId ===
      RequisitionStatuses.AWAITING_APPROVAL_OF_SENIOR_STORE_KEEPER
    ) {
      return true;
    }

    return false;
  }, [mrRequestData?.statusId]);

  const isPlantWorkshopManagerRequest = useMemo(() => {
    if (
      mrRequestData?.statusId ===
      RequisitionStatuses.AWAITING_APPROVAL_OF_PLANT_ENGINEER
    ) {
      return true;
    }

    return false;
  }, [mrRequestData?.statusId]);

  const isITAdminRequest = useMemo(() => {
    if (
      mrRequestData?.statusId ===
        RequisitionStatuses.AWAITING_APPROVAL_OF_WAREHOUSE_MANAGER &&
      isShowITAdmin
    ) {
      return true;
    }

    return false;
  }, [mrRequestData?.statusId]);

  const handleApproveNewEmailModal = useCallback((): void => {
    const { taskId, action, taskNotification } = ApprovalsParams!;
    const { username, password } = formikNewEmail.values;
    const body = {
      taskId,
      command: action,
      comment: '',
      userName: username,
      password,
    };

    itReqApproveRejectForm(body)
      .unwrap()
      .then(() => {
        taskNotification();
        refetch();
      })
      .catch(() => {
        taskErrorNotification();
      });

    dispatch(approvalsListActions.setOpenModalApprove(false));
  }, [ApprovalsParams, formikNewEmail.values]);

  const handleApproveBSAModal = useCallback((): void => {
    const { taskId, action, taskNotification } = ApprovalsParams!;
    const { comment, username, password } = formikBSA.values;
    const body = {
      taskId,
      command: action,
      comment,
      userName: username,
      password,
    };

    itReqApproveRejectForm(body)
      .unwrap()
      .then(() => {
        taskNotification();
        refetch();
      })
      .catch(() => {
        taskErrorNotification();
      });

    dispatch(approvalsListActions.setOpenModalApprove(false));
  }, [ApprovalsParams, formikBSA.values]);

  const handleApproveSimAddonModal = useCallback((): void => {
    const { taskId, action, taskNotification } = ApprovalsParams!;

    const { recommendPackage } = formikSimAddon.values;
    const body = {
      taskId,
      command: action,
      comment: '',
      existingPackage: existingPackageValue,
      recommendPackage,
    };

    itReqApproveRejectForm(body)
      .unwrap()
      .then(() => {
        taskNotification();
        refetch();
      })
      .catch(() => {
        taskErrorNotification();
      });

    dispatch(approvalsListActions.setOpenModalApprove(false));
  }, [ApprovalsParams, existingPackageValue, formikSimAddon.values]);

  const handleApproveSimCardModal = useCallback((): void => {
    const { taskId, action, taskNotification } = ApprovalsParams!;
    const { mobileNumber, reqCapValue } = formikSimCard.values;
    const body = {
      taskId: String(taskId),
      command: action,
      comment: '',
      reqCapValue,
      mobileNumber,
    };

    itReqApproveRejectForm(body)
      .unwrap()
      .then(() => {
        taskNotification();
        refetch();
      })
      .catch(() => {
        taskErrorNotification();
      });

    dispatch(approvalsListActions.setOpenModalApprove(false));
  }, [ApprovalsParams, formikSimCard.values]);

  const handleApprovePurchaseOfficerModal = useCallback((): void => {
    const { taskId, action, taskNotification } = ApprovalsParams!;

    const { comment, purchaseOfficer } = formikPurchaseOfficer.values;

    if (!purchaseOfficer) {
      errorNotification('Please Choose the Purchase Officer');
      return;
    }

    const body = {
      taskId,
      decision: action,
      comment,
      selectedApproverUserId: Number(purchaseOfficer),
      purchaseOfficerId: Number(purchaseOfficer),
    };

    requisitionApproveRejectForm(body)
      .unwrap()
      .then(() => {
        taskNotification();
        refetch();
      })
      .catch(() => {
        taskErrorNotification();
      });

    dispatch(approvalsListActions.setOpenModalApprove(false));
  }, [ApprovalsParams, formikPurchaseOfficer.values]);

  const handleApprovePlantEngineerModal = useCallback((): void => {
    const { taskId, action, taskNotification } = ApprovalsParams!;

    const { comment, plantEngineer } = formikPlantEngineer.values;

    if (!plantEngineer) {
      errorNotification('Please Choose Plant Engineer');
      return;
    }

    const body = {
      taskId,
      decision: action,
      comment,
      selectedApproverUserId: Number(plantEngineer),
    };

    requisitionApproveRejectForm(body)
      .unwrap()
      .then(() => {
        taskNotification();
        refetch();
      })
      .catch(() => {
        taskErrorNotification();
      });

    dispatch(approvalsListActions.setOpenModalApprove(false));
  }, [ApprovalsParams, formikPlantEngineer.values]);

  const handleApprovePlantWorkshopManagerModal = useCallback((): void => {
    const { taskId, action, taskNotification } = ApprovalsParams!;

    const { comment, plantWorkshopManager } = formikPlantWorkshopManager.values;

    if (!plantWorkshopManager) {
      errorNotification('Please Choose Plant Workshop Manager');
      return;
    }

    const body = {
      taskId,
      decision: action,
      comment,
      selectedApproverUserId: Number(plantWorkshopManager),
    };

    requisitionApproveRejectForm(body)
      .unwrap()
      .then(() => {
        taskNotification();
        refetch();
      })
      .catch(() => {
        taskErrorNotification();
      });

    dispatch(approvalsListActions.setOpenModalApprove(false));
  }, [ApprovalsParams, formikPlantWorkshopManager.values]);

  const handleApproveITAdminModal = useCallback((): void => {
    const { taskId, action, taskNotification } = ApprovalsParams!;

    const { comment, itAdmin } = formikITAdmin.values;

    const body = {
      taskId,
      decision: action,
      comment,
      selectedApproverUserId: Number(itAdmin),
    };

    requisitionApproveRejectForm(body)
      .unwrap()
      .then(() => {
        taskNotification();
        refetch();
      })
      .catch(() => {
        taskErrorNotification();
      });

    dispatch(approvalsListActions.setOpenModalApprove(false));
  }, [ApprovalsParams, formikITAdmin.values]);

  const onItRequisitionProcess = ({
    body,
    taskNotification,
  }: RequestProcessProps): void => {
    itReqApproveRejectForm(body)
      .unwrap()
      .then(() => {
        taskNotification();
        refetch();
      })
      .catch(() => {
        taskErrorNotification();
      });
  };

  const onHRMProcess = ({
    body,
    servicePathName,
    taskNotification,
  }: HRMProcessProps): void => {
    postApproveRejectForm({
      servicePathName,
      body,
    })
      .unwrap()
      .then(() => {
        taskNotification();
        refetch();
      })
      .catch(() => {
        taskErrorNotification();
      });
  };

  const onRequisitionProcess = ({
    body,
    taskNotification,
  }: RequisitionProcessProps): void => {
    requisitionApproveRejectForm(body)
      .unwrap()
      .then(() => {
        taskNotification();
        refetch();
      })
      .catch(() => {
        taskErrorNotification();
      });
  };

  const handleOk = useCallback((): void => {
    const { taskId, servicePathName, action, taskNotification, requestTypeId } =
      ApprovalsParams!;

    const body: FormApproveRejectRequest = {
      taskId: String(taskId),
      command: action,
      comment: action === 'Reject' ? rejectCommentValue : '',
    };

    if (requestTypeId === 'ITRequisition') {
      onItRequisitionProcess({ body, taskNotification });
    }

    if (requestTypeId === 'MRRequest') {
      const bodyRequest = {
        comment: '',
        decision: action,
        selectedApproverUserId: 0,
        taskId: String(taskId),
      };

      if (action === 'Reject') {
        bodyRequest.comment = rejectCommentValue;
      }

      onRequisitionProcess({ body: bodyRequest, taskNotification });
    }

    if (requestTypeId !== 'MRRequest' && requestTypeId !== 'ITRequisition') {
      if (action === 'Reject') {
        body.comment = rejectCommentValue;
      }
      onHRMProcess({ body, servicePathName, taskNotification });
    }

    dispatch(approvalsListActions.setOpenModalApprove(false));
  }, [ApprovalsParams, rejectCommentValue]);

  const handleCancel = (): void => {
    dispatch(approvalsListActions.setOpenModalApprove(false));
  };

  const handleApproveTask = useCallback(
    ({ requestId, taskId, requestTypeId, requestNo }: Params): void => {
      const servicePathName = deciderOfApprovePathName(requestTypeId || '');

      const isMR = requestTypeId === 'MRRequest';

      if (
        !isBSARequest &&
        !isSimAddonRequest &&
        !isPurchaseOfficerRequest &&
        !isPlantEngineerRequest &&
        !isPlantWorkshopManagerRequest &&
        !isITAdminRequest &&
        !isSimCardRequest &&
        !isNewEmailRequest
      ) {
        setMessage(`It seems like you're about to approve
          request ${isMR ? requestNo : requestId}. Are you sure you want to proceed with this action?`);
      }

      if (
        isMR &&
        mrRequestData?.statusId ===
          RequisitionStatuses.UNDER_PROCESS_WITH_PURCHASE
      ) {
        setButtonText('Complete');
      } else {
        setButtonText('Yes, Approve');
      }

      setTitle('Approve Request');
      setApprovalsParams({
        taskId,
        requestId,
        requestTypeId,
        action: ApprovalActions.APPROVE,
        servicePathName,
        taskNotification: taskApprovedNotification,
      });

      showModal();
    },
    [
      showModal,
      taskApprovedNotification,
      isBSARequest,
      isSimAddonRequest,
      isPurchaseOfficerRequest,
      isPlantEngineerRequest,
      isPlantWorkshopManagerRequest,
      isITAdminRequest,
      isSimCardRequest,
      isNewEmailRequest,
      mrRequestData?.statusId,
    ]
  );

  const handleRejectTask = useCallback(
    ({ requestId, taskId, requestTypeId, requestNo }: Params): void => {
      const servicePathName = deciderOfApprovePathName(requestTypeId || '');

      const isMR = requestTypeId === 'MRRequest';

      if (
        !isSimAddonRequest &&
        !isPurchaseOfficerRequest &&
        !isPlantEngineerRequest &&
        !isPlantWorkshopManagerRequest &&
        !isITAdminRequest
      ) {
        setMessage(`It seems like you're about to reject
          request ${isMR ? requestNo : requestId}. Are you sure you want to proceed with this action?`);
      }

      setTitle('Reject Request');
      setButtonText('Yes, Reject');
      setApprovalsParams({
        taskId,
        requestId,
        requestTypeId,
        action: ApprovalActions.REJECT,
        servicePathName,
        taskNotification: taskRejectedNotification,
      });

      showModal();
    },
    [
      showModal,
      taskRejectedNotification,
      isSimAddonRequest,
      isPurchaseOfficerRequest,
      isPlantEngineerRequest,
      isPlantWorkshopManagerRequest,
      isITAdminRequest,
    ]
  );

  return (
    <>
      <ViewResolver
        desktop={
          <ApprovalsDesktop
            handleGoToApprovals={handleGoToApprovals}
            handleESSApprovals={handleESSApprovals}
            handleMRApprovals={handleMRApprovals}
            handleApproveTask={handleApproveTask}
            handleApprovalHistory={handleApprovalHistory}
            handleRejectTask={handleRejectTask}
            activeList={activeList}
            userCollection={userCollection}
            essListCounter={essList.length}
            mrListCounter={mrList.length}
          />
        }
        mobile={
          <ApprovalsMobile
            handleGoToApprovals={handleGoToApprovals}
            handleESSApprovals={handleESSApprovals}
            handleMRApprovals={handleMRApprovals}
            handleApproveTask={handleApproveTask}
            handleApprovalHistory={handleApprovalHistory}
            handleRejectTask={handleRejectTask}
            activeList={activeList}
            userCollection={userCollection}
            essListCounter={essList.length}
            mrListCounter={mrList.length}
          />
        }
      />
      {isNewEmailRequest && ApprovalsParams?.action !== 'Reject' && (
        <EmailCreationNotification
          title={title}
          isModalOpen={stateOpenModal}
          textSuccessBtn={buttonText}
          handleCancel={handleCancel}
          handleSuccess={handleApproveNewEmailModal}
          formik={formikNewEmail}
        />
      )}
      {isSimAddonRequest && (
        <SimAddonNotification
          title={title}
          isModalOpen={stateOpenModal}
          textSuccessBtn={buttonText}
          handleCancel={handleCancel}
          handleSuccess={handleApproveSimAddonModal}
          formik={formikSimAddon}
          existingPackage={existingPackageValue}
        />
      )}
      {isSimCardRequest && (
        <SimCardNotification
          title={title}
          isModalOpen={stateOpenModal}
          textSuccessBtn={buttonText}
          handleCancel={handleCancel}
          handleSuccess={handleApproveSimCardModal}
          simCardRequest={isSimCardRequest}
          formik={formikSimCard}
        />
      )}
      {isBSARequest && ApprovalsParams?.action !== 'Reject' && (
        <BSANotification
          title={title}
          isModalOpen={stateOpenModal}
          textSuccessBtn={buttonText}
          handleCancel={handleCancel}
          handleSuccess={handleApproveBSAModal}
          formik={formikBSA}
        />
      )}
      {isPurchaseOfficerRequest && (
        <PurchaseOfficerNotification
          title={title}
          message={message}
          isModalOpen={stateOpenModal}
          textSuccessBtn={buttonText}
          handleCancel={handleCancel}
          handleSuccess={handleApprovePurchaseOfficerModal}
          formik={formikPurchaseOfficer}
        />
      )}
      {isPlantEngineerRequest && (
        <PlantEngineerNotification
          title={title}
          message={message}
          isModalOpen={stateOpenModal}
          textSuccessBtn={buttonText}
          handleCancel={handleCancel}
          handleSuccess={handleApprovePlantEngineerModal}
          formik={formikPlantEngineer}
        />
      )}
      {isPlantWorkshopManagerRequest && (
        <PlantWorkshopManagerNotification
          title={title}
          message={message}
          isModalOpen={stateOpenModal}
          textSuccessBtn={buttonText}
          handleCancel={handleCancel}
          handleSuccess={handleApprovePlantWorkshopManagerModal}
          formik={formikPlantWorkshopManager}
        />
      )}
      {isITAdminRequest && mrRequestData?.compId && (
        <ITAdminNotification
          title={title}
          message={message}
          isModalOpen={stateOpenModal}
          textSuccessBtn={buttonText}
          handleCancel={handleCancel}
          handleSuccess={handleApproveITAdminModal}
          formik={formikITAdmin}
          compId={mrRequestData?.compId}
        />
      )}
      {(!isNewEmailRequest ||
        isNewEmailRequest === (ApprovalsParams?.action === 'Reject')) &&
        (!isBSARequest ||
          isBSARequest === (ApprovalsParams?.action === 'Reject')) &&
        !isSimAddonRequest &&
        !isPurchaseOfficerRequest &&
        !isPlantEngineerRequest &&
        !isPlantWorkshopManagerRequest &&
        !isITAdminRequest &&
        !isSimCardRequest &&
        !isItRequestLoading &&
        !isMRequestLoading &&
        ApprovalsParams && (
          <RequiredNotification
            title={title}
            message={message}
            isModalOpen={stateOpenModal}
            textSuccessBtn={buttonText}
            handleCancel={handleCancel}
            handleSuccess={handleOk}
            handleCommentChange={
              ApprovalsParams?.action === 'Reject'
                ? setRejectCommentValue
                : undefined
            }
          />
        )}
      <ApprovalHistoryModal
        isOpen={isApprovalHistoryOpen}
        onClose={() => {
          setIsApprovalHistoryOpen(false);
        }}
        data={approvalHistoryData}
        users={users}
      />
    </>
  );
};

export default Approvals;
